import {
  AppOffer,
  AppOfferGroup,
  AppOfferListItem,
  IncentiveSource,
  LoyaltyOffer,
  appOfferToLoyaltyOffer,
  isAppOffer,
  isAppOfferGroup,
} from 'state/loyalty/types';
import { maybeLocaleImages } from 'utils/graphql';
import { TraceName, startTrace } from 'utils/performance/trace';
import { blockContentToPlainText } from 'utils/sanity';

import { AnimatedOfferItem } from '../loyalty-incentives-components/animated-offer-item';
import { parseIncentiveData } from '../loyalty-incentives-components/incentive-card/parse-incentive-data';
import { LoyaltyOfferGroupItem } from '../loyalty-incentives-components/loyalty-offer-group-item';
import { LoyaltyOffersItem } from '../loyalty-incentives-components/loyalty-offers-item';

export const renderOfferItem = ({
  item,
  handlePress,
  skipAnimation,
}: {
  item: AppOffer;
  handlePress: (offer: LoyaltyOffer) => void;
  skipAnimation: boolean;
}) => {
  const loyaltyOffer = appOfferToLoyaltyOffer(item);
  const incentiveData = parseIncentiveData(loyaltyOffer);
  const isPromoCode = (incentiveData.metadata || []).some(
    data => data?.value === IncentiveSource.PROMOTION_CODE
  );
  const _renderOfferItem = () => (
    <LoyaltyOffersItem
      handlePress={() => {
        startTrace(TraceName.OFFER_DETAIL_LOAD, { id: incentiveData.id, name: incentiveData.name });
        return handlePress(loyaltyOffer);
      }}
      incentiveData={incentiveData}
    />
  );
  // Animate only if skipAnimation is disabled and offer source is Promo Code campaing
  return !skipAnimation && isPromoCode ? (
    <AnimatedOfferItem setSkipAnimation={(skip: boolean) => (skipAnimation = skip)}>
      {_renderOfferItem()}
    </AnimatedOfferItem>
  ) : (
    _renderOfferItem()
  );
};

const renderOfferGroupItem = ({
  item,
  handlePress,
}: {
  item: AppOfferGroup;
  handlePress: (group: AppOfferGroup) => void;
}) => {
  const image = maybeLocaleImages(item?.localizedImage);
  const name = blockContentToPlainText(item?.name?.localeRaw);
  const description = blockContentToPlainText(item?.description?.localeRaw);

  // Collect all unique end dates from the offers in the group
  const offersEndDates = new Set<string>();
  item.offers?.forEach(offer => {
    if (offer?.endDate) {
      offersEndDates.add(offer.endDate);
    }
  });

  // Only show the end date if all offers in the group have the same end date
  let endDate: string | undefined;
  if (offersEndDates.size === 1 && item.offers.length) {
    const [commonEndDate] = offersEndDates;
    if (commonEndDate && commonEndDate.slice(0, 4) !== '9999') {
      endDate = commonEndDate;
    }
  }

  return (
    <LoyaltyOfferGroupItem
      groupId={item.cmsId}
      name={name}
      description={description}
      image={image}
      imageDescription={description}
      handlePress={() => handlePress(item)}
      endDate={endDate}
    />
  );
};

export const LoyaltyListItem = ({
  item,
  handleOfferPress,
  handleGroupPress,
  skipAnimation,
}: {
  item: AppOfferListItem;
  handleOfferPress: (offer: LoyaltyOffer) => void;
  handleGroupPress: (group: AppOfferGroup) => void;
  skipAnimation: boolean;
}) => {
  if (isAppOfferGroup(item)) {
    return renderOfferGroupItem({ item, handlePress: handleGroupPress });
  } else if (isAppOffer(item)) {
    return renderOfferItem({ item, handlePress: handleOfferPress, skipAnimation });
  }
  return null;
};
